/*
 * Media lightbox — the full-screen image viewer. See
 * snippets/media-lightbox.liquid for the markup a gallery opts into.
 *
 * The overlay is a native <dialog> opened with showModal(), so Escape, the
 * focus trap, the inert page and the top layer are the platform's job. What's
 * left for this file is the part the platform has no opinion about: paging
 * through a gallery, and the zoom/pan gesture model.
 *
 * Clicks are delegated from the document, so galleries rendered later (load
 * more, a re-rendered section, the theme editor) need no re-init; the dialog
 * itself is wired on load and again if the editor reloads its section.
 */

const TAP_SLOP = 6; // px of travel still read as a tap rather than a drag
const TAP_ZOOM = 2.5; // what a tap on the image jumps to

const controllers = new WeakMap();

function setup(dlg) {
  const stage = dlg.querySelector('[data-lightbox-stage]');
  const img = dlg.querySelector('[data-lightbox-img]');
  const counter = dlg.querySelector('[data-lightbox-counter]');
  const bar = dlg.querySelector('[data-lightbox-bar]');
  const prevBtn = dlg.querySelector('[data-lightbox-prev]');
  const nextBtn = dlg.querySelector('[data-lightbox-next]');
  const zoomInBtn = dlg.querySelector('[data-lightbox-zoom-in]');
  const zoomOutBtn = dlg.querySelector('[data-lightbox-zoom-out]');

  let items = [];
  let index = 0;
  let zoom = 1;
  let tx = 0;
  let ty = 0;

  // Cap magnification at the pixels we actually have, so zoom stops at sharp
  // rather than continuing into mush — but always allow at least 2x, or the
  // gesture would feel broken on a small source image. Derived on demand: it
  // depends on the fitted size, which a resize or a rotation moves.
  function maxZoom() {
    const fit = img.offsetWidth || 1;
    return Math.min(4, Math.max(2, (img.naturalWidth || fit) / fit));
  }

  /* ---- transform ----
     The image is moved with a transform only — never layout — so a pan stays
     on the compositor. tx/ty are screen pixels, which keeps the clamp and the
     anchor maths readable. */

  function apply() {
    img.style.setProperty('--zoom', zoom);
    img.style.setProperty('--tx', `${tx}px`);
    img.style.setProperty('--ty', `${ty}px`);
    dlg.classList.toggle('is-zoomed', zoom > 1.01);
    // aria-disabled, not disabled: the button being pressed keeps its focus
    // when it reaches its limit.
    const mark = (button, off) => {
      if (!button) return;
      if (off) button.setAttribute('aria-disabled', 'true');
      else button.removeAttribute('aria-disabled');
    };
    mark(zoomOutBtn, zoom <= 1.01);
    mark(zoomInBtn, zoom >= maxZoom() - 0.01);
  }

  // Where the image's centre sits with no transform applied. offsetLeft/Width
  // are layout values, unaffected by the current scale.
  function untransformedCenter() {
    const rect = stage.getBoundingClientRect();
    return {
      x: rect.left + img.offsetLeft + img.offsetWidth / 2,
      y: rect.top + img.offsetTop + img.offsetHeight / 2,
    };
  }

  // Keep the image covering the stage: no dragging an edge inside the frame.
  // When it's smaller than the stage in an axis, it stays centred there.
  function clamp() {
    const rect = stage.getBoundingClientRect();
    const c0 = untransformedCenter();
    const halfW = (img.offsetWidth * zoom) / 2;
    const halfH = (img.offsetHeight * zoom) / 2;
    const minX = rect.right - c0.x - halfW;
    const maxX = rect.left - c0.x + halfW;
    const minY = rect.bottom - c0.y - halfH;
    const maxY = rect.top - c0.y + halfH;
    tx = minX > maxX ? 0 : Math.min(maxX, Math.max(minX, tx));
    ty = minY > maxY ? 0 : Math.min(maxY, Math.max(minY, ty));
  }

  // Zoom to `next`, holding the point (px, py) still under the finger/cursor.
  function zoomTo(next, px, py) {
    const c0 = untransformedCenter();
    // The anchored point, in the image's own un-zoomed coordinates.
    const ux = (px - (c0.x + tx)) / zoom;
    const uy = (py - (c0.y + ty)) / zoom;

    zoom = Math.min(maxZoom(), Math.max(1, next));
    if (zoom === 1) {
      tx = 0;
      ty = 0;
    } else {
      // Put that same point back where it was, at the new scale.
      tx = px - ux * zoom - c0.x;
      ty = py - uy * zoom - c0.y;
    }
    clamp();
    apply();
  }

  function resetZoom() {
    zoom = 1;
    tx = 0;
    ty = 0;
    apply();
  }

  /* Zoom and pan without a gesture: the buttons and the keyboard. A step
     zooms about the middle of the stage - what is in view stays in view. */
  const ZOOM_STEP = 1.5;
  function zoomBy(factor) {
    const rect = stage.getBoundingClientRect();
    zoomTo(zoom * factor, rect.left + rect.width / 2, rect.top + rect.height / 2);
  }
  function panBy(dx, dy) {
    tx += dx;
    ty += dy;
    clamp();
    apply();
  }

  /* ---- slides ---- */

  function itemAt(i) {
    return items.length ? items[((i % items.length) + items.length) % items.length] : null;
  }

  function preload(i) {
    const item = itemAt(i);
    if (!item) return;
    const pre = new Image();
    if (item.dataset.lightboxSrcset) {
      pre.sizes = '100vw';
      pre.srcset = item.dataset.lightboxSrcset;
    }
    pre.src = item.dataset.lightboxSrc || '';
  }

  function show(i) {
    const item = itemAt(i);
    if (!item) return;
    index = items.indexOf(item);

    dlg.classList.add('is-loading');
    // Set the shape before the source so the box is right while the full-size
    // image is still in flight — no jump when it lands.
    if (item.dataset.lightboxWidth && item.dataset.lightboxHeight) {
      img.width = item.dataset.lightboxWidth;
      img.height = item.dataset.lightboxHeight;
    }
    // Drop the old srcset before swapping src, or the browser may keep serving
    // a candidate from the previous image.
    img.removeAttribute('srcset');
    img.src = item.dataset.lightboxSrc || '';
    if (item.dataset.lightboxSrcset) {
      img.sizes = '100vw';
      img.srcset = item.dataset.lightboxSrcset;
    }
    img.alt = item.dataset.lightboxAlt || '';
    // The image is hidden until it has loaded (the loader shows meanwhile), so
    // it must never wait for a load event that has already been and gone: one
    // the browser already holds can be complete as soon as its source is set.
    requestAnimationFrame(() => {
      if (img.complete && img.naturalWidth) loaded();
    });

    resetZoom();
    counter.textContent = items.length > 1 ? `${index + 1} / ${items.length}` : '';
  }

  function loaded() {
    dlg.classList.remove('is-loading');
  }

  function step(delta) {
    show(index + delta);
    preload(index + delta); // keep going the way the visitor is going
  }

  img.addEventListener('load', loaded);
  img.addEventListener('error', loaded);

  /* ---- open / close ---- */

  function open(trigger) {
    const gallery = trigger.closest('[data-lightbox-gallery]');
    items = gallery ? Array.from(gallery.querySelectorAll('[data-lightbox-item]')) : [trigger];

    const single = items.length < 2;
    bar.hidden = single; // one image needs neither arrows nor a counter

    const start = items.indexOf(trigger);
    show(start < 0 ? 0 : start);

    if (!dlg.open) dlg.showModal();
    if (!single) {
      preload(index + 1);
      preload(index - 1);
    }
  }

  dlg.addEventListener('click', (event) => {
    if (event.target.closest('[data-lightbox-close]')) {
      dlg.close();
      return;
    }
    // The stage is inset to clear the controls, so the strips above and below
    // it are the dialog itself. Clicking that dead space closes too, matching
    // the tap-outside-the-image behaviour handled inside the stage.
    if (event.target === dlg) dlg.close();
  });

  prevBtn.addEventListener('click', () => step(-1));
  nextBtn.addEventListener('click', () => step(1));
  if (zoomInBtn) zoomInBtn.addEventListener('click', () => zoomBy(ZOOM_STEP));
  if (zoomOutBtn) zoomOutBtn.addEventListener('click', () => zoomBy(1 / ZOOM_STEP));

  // Arrows change the image; once zoomed in they move it instead (a sixth of
  // the stage a press), since that is what there is to explore. + - and 0 zoom.
  dlg.addEventListener('keydown', (event) => {
    if (event.ctrlKey || event.metaKey || event.altKey) return; // the browser's own zoom
    const zoomed = zoom > 1.01;
    const rect = stage.getBoundingClientRect();
    const pan = { ArrowLeft: [rect.width / 6, 0], ArrowRight: [-rect.width / 6, 0], ArrowUp: [0, rect.height / 6], ArrowDown: [0, -rect.height / 6] }[event.key];
    if (zoomed && pan) {
      event.preventDefault();
      panBy(pan[0], pan[1]);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      step(1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      step(-1);
    } else if (event.key === '+' || event.key === '=') {
      event.preventDefault();
      zoomBy(ZOOM_STEP);
    } else if (event.key === '-' || event.key === '_') {
      event.preventDefault();
      zoomBy(1 / ZOOM_STEP);
    } else if (event.key === '0') {
      event.preventDefault();
      resetZoom();
    }
  });

  // Escape is handled natively; just make sure the next open starts un-zoomed.
  dlg.addEventListener('close', resetZoom);

  /* ---- gestures ----
     One pointer drags (when zoomed), two pinch. A press that travels less than
     TAP_SLOP is a tap: on the image it toggles zoom, anywhere else it closes. */

  const pointers = new Map();
  let panFrom = null;
  let pinchFrom = null;
  let travelled = 0;
  let pressedOnImage = false;

  stage.addEventListener('pointerdown', (event) => {
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    // Throws if the pointer was already released between dispatch and here;
    // capture is an optimisation for drags, never a correctness requirement.
    try {
      stage.setPointerCapture(event.pointerId);
    } catch (error) {
      /* keep going uncaptured */
    }
    travelled = 0;
    pressedOnImage = event.target === img;

    if (pointers.size === 2) {
      const [a, b] = [...pointers.values()];
      pinchFrom = {
        dist: Math.hypot(a.x - b.x, a.y - b.y) || 1,
        zoom,
      };
      panFrom = null;
      dlg.classList.remove('is-panning');
      dlg.classList.add('is-pinching');
    } else if (pointers.size === 1 && zoom > 1) {
      panFrom = { x: event.clientX, y: event.clientY, tx, ty };
      dlg.classList.add('is-panning');
    }
  });

  stage.addEventListener('pointermove', (event) => {
    const last = pointers.get(event.pointerId);
    if (!last) return;
    travelled += Math.hypot(event.clientX - last.x, event.clientY - last.y);
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (pinchFrom && pointers.size === 2) {
      const [a, b] = [...pointers.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      zoomTo(pinchFrom.zoom * (dist / pinchFrom.dist), (a.x + b.x) / 2, (a.y + b.y) / 2);
    } else if (panFrom) {
      tx = panFrom.tx + (event.clientX - panFrom.x);
      ty = panFrom.ty + (event.clientY - panFrom.y);
      clamp();
      apply();
    }
  });

  function endPointer(event) {
    if (!pointers.delete(event.pointerId)) return;

    if (pointers.size < 2) {
      pinchFrom = null;
      dlg.classList.remove('is-pinching');
    }
    if (pointers.size > 0) return;

    panFrom = null;
    dlg.classList.remove('is-panning');

    if (travelled >= TAP_SLOP) return; // that was a drag, not a tap
    if (pressedOnImage) {
      zoomTo(zoom > 1 ? 1 : TAP_ZOOM, event.clientX, event.clientY);
    } else {
      dlg.close(); // tapped the empty space around the image
    }
  }

  stage.addEventListener('pointerup', endPointer);
  stage.addEventListener('pointercancel', endPointer);

  stage.addEventListener(
    'wheel',
    (event) => {
      event.preventDefault();
      // ctrlKey is what a trackpad pinch reports; a plain wheel zooms gentler.
      const intensity = event.ctrlKey ? 0.012 : 0.0025;
      zoomTo(zoom * Math.exp(-event.deltaY * intensity), event.clientX, event.clientY);
    },
    { passive: false }
  );

  // A rotate or resize can leave a panned image with a gap at one edge.
  window.addEventListener('resize', () => {
    if (!dlg.open) return;
    clamp();
    apply();
  });

  return { open };
}

function controllerFor(dlg) {
  if (!controllers.has(dlg)) controllers.set(dlg, setup(dlg));
  return controllers.get(dlg);
}

if (!window.__mediaLightbox) {
  window.__mediaLightbox = true;

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-lightbox-item]');
    if (!trigger) return;
    // Looked up at click time, so a dialog replaced by the editor still works.
    const dlg = document.querySelector('[data-lightbox]');
    if (!dlg) return;
    event.preventDefault();
    controllerFor(dlg).open(trigger);
  });
}
